import { Spacing, ResponsiveValue } from "@shared/theme/types";

type SpacingValue = ResponsiveValue<string | number> | undefined;

type ResolvedPaddingProps = {
  p?: SpacingValue;
  pt?: SpacingValue;
  pb?: SpacingValue;
  pl?: SpacingValue;
  pr?: SpacingValue;
  px?: SpacingValue;
  py?: SpacingValue;
};

type ResolvedMarginProps = {
  m?: SpacingValue;
  mt?: SpacingValue;
  mb?: SpacingValue;
  ml?: SpacingValue;
  mr?: SpacingValue;
  mx?: SpacingValue;
  my?: SpacingValue;
};

/**
 * Resolves padding props to Chakra shorthand format.
 * Shorthand props (p, pt, etc.) take precedence over longhand (padding, paddingTop, etc.)
 * Only returns defined values.
 */
export function resolvePaddingProps(props: Spacing): ResolvedPaddingProps {
  const {
    padding,
    p,
    paddingTop,
    pt,
    paddingBottom,
    pb,
    paddingLeft,
    pl,
    paddingRight,
    pr,
    paddingX,
    px,
    paddingY,
    py,
  } = props;

  const resolved: ResolvedPaddingProps = {};

  const resolvedP = p ?? padding;
  const resolvedPt = pt ?? paddingTop;
  const resolvedPb = pb ?? paddingBottom;
  const resolvedPl = pl ?? paddingLeft;
  const resolvedPr = pr ?? paddingRight;
  const resolvedPx = px ?? paddingX;
  const resolvedPy = py ?? paddingY;

  if (resolvedP !== undefined) resolved.p = resolvedP;
  if (resolvedPt !== undefined) resolved.pt = resolvedPt;
  if (resolvedPb !== undefined) resolved.pb = resolvedPb;
  if (resolvedPl !== undefined) resolved.pl = resolvedPl;
  if (resolvedPr !== undefined) resolved.pr = resolvedPr;
  if (resolvedPx !== undefined) resolved.px = resolvedPx;
  if (resolvedPy !== undefined) resolved.py = resolvedPy;

  return resolved;
}

/**
 * Resolves margin props to Chakra shorthand format.
 * Shorthand props (m, mt, etc.) take precedence over longhand (margin, marginTop, etc.)
 * Only returns defined values.
 */
export function resolveMarginProps(props: Spacing): ResolvedMarginProps {
  const {
    margin,
    m,
    marginTop,
    mt,
    marginBottom,
    mb,
    marginLeft,
    ml,
    marginRight,
    mr,
    marginX,
    mx,
    marginY,
    my,
  } = props;

  const resolved: ResolvedMarginProps = {};

  const resolvedM = m ?? margin;
  const resolvedMt = mt ?? marginTop;
  const resolvedMb = mb ?? marginBottom;
  const resolvedMl = ml ?? marginLeft;
  const resolvedMr = mr ?? marginRight;
  const resolvedMx = mx ?? marginX;
  const resolvedMy = my ?? marginY;

  if (resolvedM !== undefined) resolved.m = resolvedM;
  if (resolvedMt !== undefined) resolved.mt = resolvedMt;
  if (resolvedMb !== undefined) resolved.mb = resolvedMb;
  if (resolvedMl !== undefined) resolved.ml = resolvedMl;
  if (resolvedMr !== undefined) resolved.mr = resolvedMr;
  if (resolvedMx !== undefined) resolved.mx = resolvedMx;
  if (resolvedMy !== undefined) resolved.my = resolvedMy;

  return resolved;
}

/**
 * Resolves both padding and margin props to Chakra shorthand format.
 */
export function resolveSpacingProps(
  props: Spacing
): ResolvedPaddingProps & ResolvedMarginProps {
  return {
    ...resolvePaddingProps(props),
    ...resolveMarginProps(props),
  };
}
